import React from 'react';
import { Fragment } from 'react';
import { useSelector } from 'react-redux';
import { Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useModal } from '@lwtears/lwt-common-frontend/lib/Modal';
import { createEvent } from 'api';
import ResourceModal from 'components/@common/Modals/ResourcePlayer';
import WorkbookPageModal from 'components/@common/Modals/WorkbookPageModal';
import { LessonPlanSection } from 'state/modules/entity/types';
import { AppStoreState } from 'types';
import { getSwitchFrameType } from 'util/resources-util';
import { calcWorkbookPreviewSize } from 'util/lesson-plan-util';

type Props = {
  lesson: {
    id: number;
    pageId: number;
    title: string;
    page: { id: number; bookId: number; pageNumber: number };
    sections: Array<LessonPlanSection>;
  };
  className: string;
};

const handlePreviewResource = (resource, currentWorkbook, openDraggableModal) => {
  if (resource.type.type === 'workbookPage') {
    const { width, height } = calcWorkbookPreviewSize(
      window.innerHeight,
      currentWorkbook.orientation === 'portrait'
    );

    openDraggableModal({
      body: (
        <WorkbookPageModal
          workbook={currentWorkbook}
          currentPage={resource.pageNumber}
          width={width}
        />
      ),
      styles: { backgroundColor: '#EEEEEE', height: height, width: width },
      title: `${currentWorkbook.title} p. ${resource.pageNumber}`
    });
  } else {
    //make sure these two properties are at the root level
    //so ResourceModal can see them (versus buried in data)
    resource.aspectRatio = resource.data?.aspectRatio;
    resource.heightAdjust = resource.data?.heightAdjust;
    openDraggableModal({
      titleText: resource.title,
      body: (
        <ResourceModal
          selectedResource={resource}
          frameType={getSwitchFrameType(resource.type.type, resource.data.mediaType)}
          handleClose={() => null}
          eventLogger={() =>
            createEvent('accessResource', {
              section: 'assignmentPreview',
              resourceId: resource.id,
              resourceType: resource.type.type
            })
          }
        />
      ),
      options: { closeOnBackdropClick: true }
    });
  }
};

export const DayCardContent = ({ lesson, ...props }: Props) => {
  const { view } = useSelector(({ lessonPlanner }: AppStoreState) => lessonPlanner);
  const [, { openDraggableModal }] = useModal();
  const currentWorkbook: any = useSelector(({ app }: AppStoreState) => app.currentWorkbook);
  // const getAssignedCharacter = () => view === LESSON_PLANNER_VIEW.DAY ? 'Assigned' : 'A';
  const { assignments, lessonTitle } = useStyles()({ dayView: view === 'day' });

  return (
    <div {...props}>
      {lesson?.sections.map((section, idx) => (
        <Fragment key={`${idx}-section`}>
          <Typography key={`${idx}-sectionTitle`} className={lessonTitle} variant="subtitle2">
            {section.title}
          </Typography>
          <ul style={{ padding: 0, marginLeft: 0, listStyle: 'none' }}>
            {section.activities?.map((activity, i) => (
              <li key={`${i}-activityList`} style={{ marginBottom: '15px', lineHeight: 1.4 }}>
                <span
                  className={assignments}
                  key={`${i}-activity`}
                  onClick={() =>
                    handlePreviewResource(activity, currentWorkbook, openDraggableModal)
                  }
                >
                  {activity?.title}
                </span>
              </li>
            ))}
          </ul>
        </Fragment>
      ))}
    </div>
  );
};

const useStyles = () =>
  makeStyles(() => ({
    assignments: ({ dayView }: any) => ({
      display: 'flex',
      flex: 1,
      justifyContent: 'space-between',
      cursor: 'pointer',
      fontSize: 15,
      color: '#454545',
      ...(dayView && {
        justifyContent: 'flex-start'
      }),
      '&:hover': {
        textDecoration: 'underline'
      }
    }),
    lessonTitle: {
      marginTop: '5px !important',
      //textDecoration: 'underline',
      fontSize: '16px !important',
      marginBottom: '7px !important'
    }
  }));
